import { useEffect, useRef, useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { GIS_PRESET_PERIODS, type GisPeriod, type GisPresetPeriod } from '../../api/types'
import { periodLabel } from '../../lib/period-label'

interface PeriodDropdownProps {
  period: GisPeriod
  dateFrom?: string | undefined
  dateTo?: string | undefined
  onPeriodChange: (period: GisPeriod) => void
  onCustomRange: (from: string, to: string) => void
}

const LABELS: Record<GisPresetPeriod, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  fy: 'Fiscal Year',
}

/** Thin wrapper over the shared helper so the trigger == the Coverage header phrase. */
function triggerLabel(period: GisPeriod, from?: string, to?: string): string {
  return periodLabel(period, from, to)
}

const INPUT = 'mb-2 w-full rounded-md border border-slate-900/10 px-2 py-1 text-xs'
const ITEM = 'block w-full rounded-md px-2.5 py-1.5 text-left text-[12px]'

/**
 * Compact dropdown date-range control (active style). One trigger expands to a list of
 * presets + "Custom Range"; choosing Custom Range reveals an inline from/to picker.
 * Drop-in swappable with the segmented `PeriodControl` (same props) — see PanelHeader.
 */
export function PeriodDropdown({ period, dateFrom, dateTo, onPeriodChange, onCustomRange }: PeriodDropdownProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(false)
  const [from, setFrom] = useState(dateFrom ?? '')
  const [to, setTo] = useState(dateTo ?? '')

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setCustom(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const pickPreset = (p: GisPeriod) => {
    onPeriodChange(p)
    setOpen(false)
    setCustom(false)
  }
  // ISO date strings compare lexically, so `from <= to` is a valid chronological check.
  const valid = from !== '' && to !== '' && from <= to

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-[30px] items-center gap-[6px] rounded-[9px] border border-gis-line bg-white px-[10px] text-[12px] font-medium text-gis-ink-2 transition-colors hover:border-[#d6dbe6]"
      >
        {triggerLabel(period, dateFrom, dateTo)}
        <ChevronDown className="h-[13px] w-[13px] text-gis-muted" aria-hidden />
      </button>
      {open && (
        <div className="absolute top-[34px] right-0 z-30 w-[172px] rounded-[12px] border border-slate-900/10 bg-white p-1 shadow-xl">
          {custom ? (
            <div className="p-1.5">
              <label className="text-muted-text block text-[10px] font-bold tracking-wider uppercase">From</label>
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
              <label className="text-muted-text block text-[10px] font-bold tracking-wider uppercase">To</label>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={INPUT} />
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setCustom(false)}
                  className="flex-1 rounded-md border border-slate-900/10 py-1 text-xs font-semibold hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={!valid}
                  onClick={() => {
                    onCustomRange(from, to)
                    setOpen(false)
                    setCustom(false)
                  }}
                  className="flex-1 rounded-md bg-slate-900 py-1 text-xs font-bold text-white disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : (
            <>
              {GIS_PRESET_PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => pickPreset(p)}
                  className={`${ITEM} ${period === p ? 'bg-blue-500 font-semibold text-white' : 'hover:bg-slate-100'}`}
                >
                  {LABELS[p]}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCustom(true)}
                className={`${ITEM} ${period === 'custom' ? 'bg-blue-500 font-semibold text-white' : 'hover:bg-slate-100'}`}
              >
                Custom Range
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
